import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined"; // Branch
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined"; // Department
import MergeTypeOutlinedIcon from "@mui/icons-material/MergeTypeOutlined"; // Branch Department
import ApartmentRoundedIcon from "@mui/icons-material/ApartmentRounded"; // Organization
import DynamicFormOutlinedIcon from "@mui/icons-material/DynamicFormOutlined"; // Organization Forms
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined"; // Tags
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import { Avatar, Box, Stack, useMediaQuery, Button, Typography } from "@mui/material";
import { APP_ROUTES } from "../../../config/appRoutes";
import { branchThunk, branchDepartmentThunk, departmentThunk } from "../../../components/general/generalThunk";
import { useDispatch } from "react-redux";
import { resetGeneralState } from "../../../components/general/generalSlice";
import { resetState } from "../../contact/contactSlice";
import { getOrganizationsThunk } from "../../contact/contactThunk";
import { QR, QrActionButton } from "../../qrCode/components/qrUi";

// Header card for the Organization area (Organization / Forms / Branch / Department / Branch Dept / Tags).
// Optional: `stats` (cards inside the header) and `children` (page content inside the same card).
// Optional: `onCreate` — when a page passes it, "Creation" opens that page's popup instead of navigating.
export default function PageButtons({ stats = [], children, onCreate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [activePath, setActivePath] = useState(location.pathname);
  const [expanded, setExpanded] = useState("Organization");

  const sections = [
    { label: "Organization", routes: APP_ROUTES.ORGANIZATION, icon: <ApartmentRoundedIcon /> },
    { label: "Organization Forms", routes: APP_ROUTES.FORM, icon: <DynamicFormOutlinedIcon /> },
    { label: "Branch", routes: APP_ROUTES.BRANCH, icon: <AccountTreeOutlinedIcon /> },
    { label: `${!isMobile ? "Department" : "Dept"}`, routes: APP_ROUTES.DEPARTMENT, icon: <BusinessOutlinedIcon /> },
    { label: `Branch ${!isMobile ? "Department" : "Dept"}`, routes: APP_ROUTES.BRANCH_DEPARTMENT, icon: <MergeTypeOutlinedIcon /> },
    { label: "Tags", routes: APP_ROUTES.TAGS, icon: <LocalOfferOutlinedIcon /> },
  ];

  const knownPaths = new Set(
    sections.flatMap((s) => [s.routes.LIST, s.routes.CREATION])
  );

  // On mount: redirect if path is unknown
  useEffect(() => {
    if (!knownPaths.has(location.pathname)) {
      navigate(APP_ROUTES.BRANCH.LIST, { replace: true });
      setActivePath(APP_ROUTES.BRANCH.LIST);
      setExpanded("Branch");
    } else {
      setActivePath(location.pathname);
      const match = sections.find(
        (s) =>
          location.pathname === s.routes.LIST ||
          location.pathname === s.routes.CREATION
      );
      if (match) setExpanded(match.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track path changes to update active button & expanded section
  useEffect(() => {
    setActivePath(location.pathname);
    const match = sections.find(
      (s) =>
        location.pathname === s.routes.LIST ||
        location.pathname === s.routes.CREATION
    );
    if (match) setExpanded(match.label);
  }, [location.pathname]);

  // Fetch all required data on mount
  useEffect(() => {
    dispatch(branchThunk());
    dispatch(departmentThunk());
    dispatch(branchDepartmentThunk());
    dispatch(getOrganizationsThunk());
  }, [dispatch]);

  // Handle click on main section or actions
  const handleClick = (path) => {
    navigate(path);

    // Reset state and refetch data to avoid empty lists
    dispatch(resetGeneralState());
    dispatch(resetState());

    // Refetch data
    dispatch(branchThunk());
    dispatch(departmentThunk());
    dispatch(branchDepartmentThunk());
    dispatch(getOrganizationsThunk());

    setActivePath(path);

    // Expand the correct section
    const match = sections.find(
      (s) => s.routes.LIST === path || s.routes.CREATION === path
    );
    if (match) setExpanded(match.label);
  };

  // ---------- Underline tab style (same as Lead Management) ----------
  const activeGlow = "linear-gradient(180deg, rgba(25,135,84,0) 0%, rgba(25,135,84,0.08) 100%)";
  const tabSx = (active) => ({
    position: "relative",
    flexShrink: 0,
    height: 52,
    px: { xs: 1.25, sm: 1.75 },
    gap: 1.1,
    borderRadius: "12px 12px 0 0",
    textTransform: "none",
    fontSize: 14.5,
    fontWeight: active ? 800 : 600,
    whiteSpace: "nowrap",
    color: active ? QR.dark : "#64748b",
    background: active ? activeGlow : "transparent",
    transition: "all .2s ease",
    "& .tab-ico": {
      width: 30,
      height: 30,
      borderRadius: 2.25,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: active ? "#fff" : "#94a3b8",
      background: active ? `linear-gradient(135deg, ${QR.main}, ${QR.light})` : "#f1f5f9",
      boxShadow: active ? `0 6px 12px -6px ${QR.main}` : "none",
      transition: "all .2s ease",
      "& svg": { fontSize: 17 },
    },
    "&::after": {
      content: "\"\"",
      position: "absolute",
      left: 12,
      right: 12,
      bottom: -1,
      height: 3,
      borderRadius: "3px 3px 0 0",
      background: active ? `linear-gradient(90deg, ${QR.main}, ${QR.light})` : "transparent",
      boxShadow: active ? `0 -2px 8px ${QR.main}66` : "none",
      transition: "all .2s ease",
    },
    "&:hover": {
      background: active ? activeGlow : "#f8fafc",
      color: active ? QR.dark : "#0f172a",
      "& .tab-ico": active ? {} : { color: QR.main, bgcolor: `${QR.main}14` },
    },
  });


  return (
    <Box
      sx={{
        position: "relative",
        p: { xs: 1.5, sm: 2, md: 2.5 },
        borderRadius: 3,
        border: `1px solid ${QR.border}`,
        bgcolor: "#fff",
        boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
        // pages that render their content below the header get a little breathing room
        mb: children ? 0 : 2,
      }}
    >
      {/* Title + List / Create switch for the current section */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{
              width: { xs: 40, sm: 46 },
              height: { xs: 40, sm: 46 },
              borderRadius: 2.5,
              color: "#fff",
              background: `linear-gradient(135deg, ${QR.main}, ${QR.light})`,
              boxShadow: `0 8px 18px -8px ${QR.main}`,
            }}
          >
            <CorporateFareIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} color="#0f172a" lineHeight={1.2} sx={{ fontSize: { xs: 17, sm: 20 } }}>
              Organization
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.4 }}>
              Manage organizations, forms, branches, departments and tags
            </Typography>
          </Box>
        </Stack>

        {/* Actions for the expanded section */}
        {sections.map(
          (section) =>
            expanded === section.label && (
              <Box
                key={`${section.label}-actions`}
                sx={{ width: { xs: "100%", sm: "auto" }, "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } } }}
              >
                {/* One context button: list page → "Create", create page → "Back to List" */}
                {activePath === section.routes.CREATION ? (
                  <QrActionButton
                    variant="solid"
                    color="#fc8006" // logo orange
                    gradientTo="#fb923c"
                    icon={<ArrowBackIcon sx={{ fontSize: 17 }} />}
                    label="Back to List"
                    onClick={() => handleClick(section.routes.LIST)}
                  />
                ) : (
                  <QrActionButton
                    variant="solid"
                    color={QR.blue}
                    gradientTo="#3b82f6"
                    icon={<AddCircleOutlineIcon sx={{ fontSize: 17 }} />}
                    label={`Create ${section.label}`}
                    onClick={() => (onCreate ? onCreate() : handleClick(section.routes.CREATION))}
                  />
                )}
              </Box>
            )
        )}
      </Box>

      {/* Section tabs (underline) */}
      <Box
        ref={scrollRef}
        sx={{
          mt: 2,
          mx: { xs: -1.5, sm: -2, md: -2.5 },
          px: { xs: 1, sm: 1.5, md: 2 },
          display: "flex",
          gap: 0.5,
          overflowX: "auto",
          scrollBehavior: "smooth",
          background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
          borderTop: `1px solid ${QR.border}`,
          borderBottom: `1px solid ${QR.border}`,
          ...(stats.length === 0 && !children ? { mb: { xs: -1.5, sm: -2, md: -2.5 }, borderBottom: "none" } : {}),
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {sections.map((section, idx) => {
          const isSectionActive =
            activePath === section.routes.LIST ||
            activePath === section.routes.CREATION;
          return (
            <Button
              key={idx}
              onClick={() => handleClick(section.routes.LIST)} // always go to List
              aria-current={isSectionActive ? "page" : undefined}
              sx={tabSx(isSectionActive)}
            >
              <Box component="span" className="tab-ico">{section.icon}</Box>
              {section.label}
            </Button>
          );
        })}
      </Box>

      {/* Stats cards (only when the page passes stats) */}
      {stats.length > 0 && (
        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${stats.length}, minmax(0, 1fr))` },
            gap: 1.5,
          }}
        >
          {stats.map((s, idx) => (
            <Stack
              key={s.label}
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                gridColumn: { xs: stats.length % 2 === 1 && idx === stats.length - 1 ? "span 2" : "auto", md: "auto" },
                minWidth: 0,
                p: { xs: 1.25, sm: 1.5 },
                borderRadius: 3,
                bgcolor: "#fff",
                border: `1px solid ${QR.border}`,
                borderTop: `3px solid ${s.color}`,
                boxShadow: "0 1px 3px rgba(16,24,40,0.06)",
              }}
            >
              <Avatar variant="rounded" sx={{ width: 40, height: 40, borderRadius: 2, color: s.color, bgcolor: `${s.color}14` }}>
                {s.icon}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap display="block">
                  {s.label}
                </Typography>
                <Typography variant="h6" fontWeight={800} lineHeight={1.2} color="#0f172a">
                  {s.value}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      )}

      {/* Page content inside the same card */}
      {children && (
        <Box sx={{ mt: stats.length > 0 ? 2.5 : 1.5, pt: 0.5, borderTop: stats.length > 0 ? `1px solid ${QR.border}` : "none" }}>
          {children}
        </Box>
      )}
    </Box>
  );
}
